import { createEffect, createResource, onCleanup, onMount } from "solid-js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { PlanBasemap } from "@energyplatform/client";
import { t } from "../i18n.ts";
import { placeLabels } from "../plans.ts";
import { api } from "../services.ts";

/** Where a map plan opens. */
export interface MapView {
  latitude: number;
  longitude: number;
  zoom: number;
}

/** A point on a map plan as shown: where, its colour, and optionally its label and layers. */
export interface MapMark {
  pointId: string;
  latitude: number;
  longitude: number;
  color: string;
  title: string;
  /** Values to show: one row per point (a device's phases and their sum share one marker). */
  label?: {
    rows: { name: string; value: string; warn: boolean }[];
    dashed: boolean;
  };
  /** Radius in px of the power halo. */
  halo?: number;
  haloColor?: string;
  ring?: boolean;
  dim?: boolean;
  selected?: boolean;
}

const MAX_ZOOM = 20;

/**
 * The map under a plan: OpenStreetMap, or aerial photos of Poland from the national geoportal (GUGiK orthophoto, open
 * data; outside Poland it is blank).
 */
export function basemapLayer(
  kind: PlanBasemap,
  orthophoto: "platform" | "browser",
): L.TileLayer {
  if (kind === "osm") {
    return L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      className: "street-tiles",
      maxZoom: MAX_ZOOM,
      maxNativeZoom: 19,
      attribution: "© OpenStreetMap",
    });
  }
  // Through the platform, which fetches each tile from the slow geoportal once and keeps it, where the server can
  // reach the geoportal; otherwise straight from it. 512 px tiles mean four times fewer requests either way.
  return orthophoto === "platform"
    ? L.tileLayer("/api/v1/geo/tiles/orto/{z}/{x}/{y}", {
        tileSize: 512,
        zoomOffset: -1,
        minZoom: 1,
        maxZoom: MAX_ZOOM,
        maxNativeZoom: MAX_ZOOM,
        attribution: t("plans.map.orthoAttribution"),
      })
    : L.tileLayer.wms(
        "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/HighResolution",
        {
          layers: "Raster",
          format: "image/jpeg",
          version: "1.3.0",
          tileSize: 512,
          maxZoom: MAX_ZOOM,
          attribution: t("plans.map.orthoAttribution"),
        },
      );
}

// Leaflet sets SVG attributes, where CSS variables do not apply: the theme's colours are read when drawn.
const cssColor = (value: string) => {
  const name = /^var\((--[^)]+)\)$/.exec(value)?.[1];
  return name
    ? getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || "#888"
    : value;
};

// The marker is built from DOM nodes: names are user text and must not be parsed as HTML.
function markerElement(m: MapMark): HTMLElement {
  const box = document.createElement("div");
  box.className = `pmap${m.dim ? " dim" : ""}${m.selected ? " sel" : ""}`;
  const dot = document.createElement("i");
  dot.style.background = m.color;
  box.append(dot);
  if (m.label) {
    const tag = document.createElement("span");
    tag.className = `pmap-tag${m.label.dashed ? " stale" : ""}${m.ring ? " alarm" : ""}`;
    const name = document.createElement("small");
    name.textContent = m.title;
    tag.append(name);
    for (const row of m.label.rows) {
      const value = document.createElement("b");
      value.textContent = row.name ? `${row.name}: ${row.value}` : row.value;
      if (row.warn) {
        value.style.color = "var(--warn)";
      }
      tag.append(value);
    }
    box.append(tag);
  }
  return box;
}

/**
 * A plan on a map: points as markers with their values, power halos and alarm rings, like on an image plan. In the
 * wizard it also places points (click), moves them (drag) and reports the view, which the plan opens at.
 */
export function PlanMapView(props: {
  basemap: PlanBasemap;
  view: MapView;
  marks: MapMark[];
  height?: string;
  label: string;
  /** Open as close as possible with every mark in view, instead of at the stored view. */
  fit?: boolean;
  onMapClick?: (latitude: number, longitude: number) => void;
  onMarkClick?: (pointId: string) => void;
  onMarkMoved?: (pointId: string, latitude: number, longitude: number) => void;
  onView?: (view: MapView) => void;
}) {
  let host!: HTMLDivElement;
  let map: L.Map | undefined;
  let tiles: L.TileLayer | undefined;
  let layer: L.LayerGroup | undefined;
  let fitted = false;
  let markers: L.Marker[] = [];
  // Labels are measured where they are shown and moved so that they do not cover each other or other markers;
  // again after every zoom, since their distances on screen change.
  const arrange = () => {
    if (!map) {
      return;
    }
    const shown = markers
      .map((mk) => ({
        at: map!.latLngToContainerPoint(mk.getLatLng()),
        tag: mk.getElement()?.querySelector<HTMLElement>(".pmap-tag") ?? null,
      }))
      .filter((x): x is { at: L.Point; tag: HTMLElement } => x.tag !== null);
    const offsets = placeLabels(
      shown.map((x) => ({
        x: x.at.x,
        y: x.at.y,
        w: x.tag.offsetWidth,
        h: x.tag.offsetHeight,
      })),
      9,
      4,
    );
    shown.forEach((x, i) => {
      // The icon is 18 px with its centre at 9, 9.
      x.tag.style.left = `${9 + offsets[i]!.dx}px`;
      x.tag.style.top = `${9 + offsets[i]!.dy}px`;
    });
  };
  onMount(() => {
    map = L.map(host, {
      maxZoom: MAX_ZOOM,
      scrollWheelZoom: !!props.onView,
    }).setView([props.view.latitude, props.view.longitude], props.view.zoom);
    layer = L.layerGroup().addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) =>
      props.onMapClick?.(e.latlng.lat, e.latlng.lng),
    );
    map.on("zoomend", arrange);
    map.on("moveend", () => {
      const c = map!.getCenter();
      props.onView?.({
        latitude: Math.round(c.lat * 1e6) / 1e6,
        longitude: Math.round(c.lng * 1e6) / 1e6,
        zoom: map!.getZoom(),
      });
    });
    onCleanup(() => map?.remove());
  });
  const [basemaps] = createResource(() => api.geo.basemaps());
  createEffect(() => {
    const kind = props.basemap;
    const via =
      basemaps()?.orthophoto ?? (basemaps.error ? "browser" : undefined);
    if (map && (kind === "osm" || via)) {
      tiles?.remove();
      tiles = basemapLayer(kind, via ?? "browser").addTo(map);
      tiles.bringToBack();
    }
  });
  createEffect(() => {
    const marks = props.marks;
    if (!map || !layer) {
      return;
    }
    layer.clearLayers();
    markers = [];
    if (props.fit && !fitted && marks.length > 0) {
      fitted = true;
      // Labels sit to the right of their marker: leave room for them there.
      map.fitBounds(
        L.latLngBounds(marks.map((m) => [m.latitude, m.longitude])),
        {
          paddingTopLeft: [40, 40],
          paddingBottomRight: [180, 40],
          maxZoom: MAX_ZOOM - 1,
        },
      );
    }
    for (const m of marks) {
      const at: L.LatLngTuple = [m.latitude, m.longitude];
      if (m.halo && m.halo > 0) {
        L.circleMarker(at, {
          radius: m.halo,
          stroke: false,
          fillColor: cssColor(m.haloColor ?? m.color),
          fillOpacity: 0.22,
          interactive: false,
        }).addTo(layer);
      }
      if (m.ring) {
        L.circleMarker(at, {
          radius: 16,
          color: cssColor("var(--crit)"),
          weight: 2.5,
          fill: false,
          interactive: false,
        }).addTo(layer);
      }
      const marker = L.marker(at, {
        icon: L.divIcon({
          className: "pmap-icon",
          html: markerElement(m),
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }),
        title: m.title,
        keyboard: true,
        draggable: !!props.onMarkMoved,
      }).addTo(layer);
      markers.push(marker);
      marker.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        props.onMarkClick?.(m.pointId);
      });
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        props.onMarkMoved?.(m.pointId, p.lat, p.lng);
      });
    }
    arrange();
  });
  return (
    <div
      ref={host}
      class="map"
      style={{
        height: props.height ?? "460px",
        cursor: props.onMapClick ? "crosshair" : undefined,
      }}
      role="region"
      aria-label={props.label}
    />
  );
}
